import { combineReducers } from "redux";
import { createReducer, makeFetchReducer } from "utils/reducer";
import { Types } from "actions/patient-outreaches";
import { createSelector } from "reselect";

const DEFAULT_FILTERS = {
  outreachStates: ["Unallocated"],
  companyGuids: [],
  teamGuids: [],
  allocatedCoachGuids: [],
  productLines: [],
  cycleCount: null,
  spanishOnly: false,
  inTcpaWindowNow: false,
  patientLookup: ""
};

const updateRowIf = (state, patientGuid, patch) =>
  state.map(r => (r.patientGuid === patientGuid ? { ...r, ...patch } : r));

const UNALLOCATE_PATCH = {
  allocatedCoachGuid: null,
  allocatedCoachName: null,
  dtAllocated: null,
  outreachState: "Unallocated"
};
const REMOVE_FROM_SYNTHPOP_PATCH = {
  ...UNALLOCATE_PATCH,
  isSynthpopWaitingOnCallback: false
};

const applyBulkRowUpdates = (state, results, patch) => {
  if (!Array.isArray(results) || results.length === 0) return state;
  const succeededGuids = new Set(
    results.filter(r => r.outcome === "Succeeded").map(r => r.patientGuid)
  );
  if (succeededGuids.size === 0) return state;
  return state.map(r =>
    succeededGuids.has(r.patientGuid) ? { ...r, ...patch } : r
  );
};

const applyBulkRowRemovals = (state, results, outcomeName) => {
  if (!Array.isArray(results) || results.length === 0) return state;
  const removedGuids = new Set(
    results.filter(r => r.outcome === outcomeName).map(r => r.patientGuid)
  );
  if (removedGuids.size === 0) return state;
  return state.filter(r => !removedGuids.has(r.patientGuid));
};

const rows = createReducer([], {
  // Clear immediately so stale rows do not linger across a refetch —
  // otherwise a filter change can render the old rows beside a
  // "No patients match" message if responses interleave.
  [Types.REQUEST_PATIENT_OUTREACH_LIST]: () => [],
  [Types.RESPONSE_PATIENT_OUTREACH_LIST]: (_, { payload }) =>
    payload?.rows ?? [],
  [Types.PATIENT_OUTREACH_UNALLOCATE]: (state, { payload, meta }) => {
    if (!payload?.succeeded) return state;
    return updateRowIf(state, meta.patientGuid, UNALLOCATE_PATCH);
  },
  [Types.PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP]: (state, { payload, meta }) => {
    if (!payload?.succeeded) return state;
    return updateRowIf(state, meta.patientGuid, REMOVE_FROM_SYNTHPOP_PATCH);
  },
  [Types.PATIENT_OUTREACH_POSTPONE]: (state, { payload, meta }) => {
    if (!payload?.succeeded || !payload?.removedFromTodayPool) return state;
    return state.filter(r => r.patientGuid !== meta.patientGuid);
  },
  [Types.PATIENT_OUTREACH_UNALLOCATE_BULK]: (state, { payload }) =>
    applyBulkRowUpdates(state, payload?.results, UNALLOCATE_PATCH),
  [Types.PATIENT_OUTREACH_REMOVE_FROM_SYNTHPOP_BULK]: (state, { payload }) =>
    applyBulkRowUpdates(state, payload?.results, REMOVE_FROM_SYNTHPOP_PATCH),
  [Types.PATIENT_OUTREACH_POSTPONE_BULK]: (state, { payload }) =>
    applyBulkRowRemovals(state, payload?.results, "Postponed")
});

const totalMatching = createReducer(0, {
  // Intentionally NOT cleared on REQUEST — keep the prior count visible during
  // a refetch so the pagination footer doesn't briefly show "Page 2 of 1" while
  // the response is in flight. Rows are still cleared on REQUEST so the table
  // body shows the loading state.
  [Types.RESPONSE_PATIENT_OUTREACH_LIST]: (_, { payload }) =>
    payload?.totalMatching ?? 0,
  [Types.PATIENT_OUTREACH_POSTPONE]: (state, { payload }) =>
    payload?.succeeded && payload?.removedFromTodayPool
      ? Math.max(0, state - 1)
      : state,
  [Types.PATIENT_OUTREACH_POSTPONE_BULK]: (state, { payload }) => {
    const removed = (payload?.results ?? []).filter(
      r => r.outcome === "Postponed"
    ).length;
    return Math.max(0, state - removed);
  }
});

const globalCounts = createReducer(null, {
  [Types.RESPONSE_PATIENT_OUTREACH_COUNTS]: (_, { payload }) => payload
});

const EMPTY_FILTER_OPTIONS = { companies: [], teams: [], coaches: [] };
const filterOptions = createReducer(EMPTY_FILTER_OPTIONS, {
  [Types.RESPONSE_PATIENT_OUTREACH_FILTER_OPTIONS]: (_, { payload }) =>
    payload || EMPTY_FILTER_OPTIONS
});

const eligibilityRules = createReducer([], {
  [Types.RESPONSE_ELIGIBILITY_RULES]: (_, { payload }) => payload ?? []
});

const currentEligibilityEvaluation = createReducer(null, {
  [Types.REQUEST_ELIGIBILITY_EVALUATION]: () => null,
  [Types.RESPONSE_ELIGIBILITY_EVALUATION]: (_, { payload }) => payload,
  [Types.ERROR_ELIGIBILITY_EVALUATION]: () => null
});

// HCPCS eligibility is fetched in parallel with the main evaluation — separate
// slice so the FE can show a spinner in just the HCPCS section without holding
// the rest of the lookup. Cleared whenever a new evaluation request fires so
// stale HCPCS data doesn't render against a different patient.
const currentHcpcsEligibility = createReducer(null, {
  [Types.REQUEST_ELIGIBILITY_EVALUATION]: () => null,
  [Types.REQUEST_ELIGIBILITY_HCPCS]: () => null,
  [Types.RESPONSE_ELIGIBILITY_HCPCS]: (_, { payload }) => payload,
  [Types.ERROR_ELIGIBILITY_HCPCS]: () => null
});

const filters = createReducer(DEFAULT_FILTERS, {
  [Types.SET_PATIENT_OUTREACH_FILTERS]: (_, { payload }) => ({
    ...DEFAULT_FILTERS,
    ...payload
  })
});

const page = createReducer(1, {
  [Types.SET_PATIENT_OUTREACH_PAGE]: (_, { payload }) => payload,
  [Types.SET_PATIENT_OUTREACH_FILTERS]: () => 1
});

const pageSize = createReducer(50, {
  [Types.SET_PATIENT_OUTREACH_PAGESIZE]: (_, { payload }) =>
    Math.min(payload, 100)
});

const DEFAULT_SORT = { sortBy: "dtDateLoaded", sortDir: "DESC" };
const sort = createReducer(DEFAULT_SORT, {
  [Types.SET_PATIENT_OUTREACH_SORT]: (_, { payload }) => payload
});

const isFetchingList = makeFetchReducer(
  [
    Types.REQUEST_PATIENT_OUTREACH_LIST,
    Types.RESPONSE_PATIENT_OUTREACH_LIST,
    Types.ERROR_PATIENT_OUTREACH_LIST
  ],
  true
);

const isFetchingEvaluation = makeFetchReducer(
  [
    Types.REQUEST_ELIGIBILITY_EVALUATION,
    Types.RESPONSE_ELIGIBILITY_EVALUATION,
    Types.ERROR_ELIGIBILITY_EVALUATION
  ],
  true
);

const isFetchingHcpcs = makeFetchReducer(
  [
    Types.REQUEST_ELIGIBILITY_HCPCS,
    Types.RESPONSE_ELIGIBILITY_HCPCS,
    Types.ERROR_ELIGIBILITY_HCPCS
  ],
  true
);

export default combineReducers({
  rows,
  totalMatching,
  globalCounts,
  filterOptions,
  eligibilityRules,
  currentEligibilityEvaluation,
  currentHcpcsEligibility,
  filters,
  page,
  pageSize,
  sort,
  isFetchingList,
  isFetchingEvaluation,
  isFetchingHcpcs
});

const sliceSelector = state => state.get("patient-outreaches");

export const selectors = {
  getRows: createSelector([sliceSelector], s => s.rows),
  getTotalMatching: createSelector([sliceSelector], s => s.totalMatching),
  getGlobalCounts: createSelector([sliceSelector], s => s.globalCounts),
  getFilterOptions: createSelector([sliceSelector], s => s.filterOptions),
  getEligibilityRules: createSelector([sliceSelector], s => s.eligibilityRules),
  getCurrentEligibilityEvaluation: createSelector(
    [sliceSelector],
    s => s.currentEligibilityEvaluation
  ),
  getFilters: createSelector([sliceSelector], s => s.filters),
  getPage: createSelector([sliceSelector], s => s.page),
  getPageSize: createSelector([sliceSelector], s => s.pageSize),
  getSort: createSelector([sliceSelector], s => s.sort),
  getIsFetchingList: createSelector([sliceSelector], s => s.isFetchingList),
  getIsFetchingEvaluation: createSelector(
    [sliceSelector],
    s => s.isFetchingEvaluation
  ),
  getCurrentHcpcsEligibility: createSelector(
    [sliceSelector],
    s => s.currentHcpcsEligibility
  ),
  getIsFetchingHcpcs: createSelector(
    [sliceSelector],
    s => s.isFetchingHcpcs
  )
};
